import { ExplorerFolder } from '@app/shared/types/models/files/explorer-folder.model';
import { FileInfo } from '@app/shared/types/models/files/file-info';

export type FileExplorerSort = 'name' | 'date' | 'size';

export interface FileExplorerFolderRow {
  folder: ExplorerFolder;
  labelKey?: string;
}

/**
 * System/semantic folder types kept visible regardless of structural hiding:
 * Organization (24), task semantic groups (17/15/14), and their authorized entry leaves (22/5/4).
 * Any other system `type` (including the structural Projects/11, Tasks/12, Locations/16) is hidden.
 * Folders without `isSystem` or without a `type` (older API) are always shown read-only.
 */
const VISIBLE_SYSTEM_TYPES = new Set([24, 17, 15, 14, 22, 5, 4]);

const TASK_SEMANTIC_LABEL_KEYS: Record<number, string> = {
  17: 'files.explorer.categories.description',
  15: 'files.explorer.categories.comments',
  14: 'files.explorer.categories.reports'
};

/** Matches the OrganizationSystemFolderKind values verified in the current BE. */
const SYSTEM_KEY_LABEL_KEYS: Record<number, string> = {
  1: 'files.explorer.categories.description',
  2: 'files.explorer.categories.reports',
  3: 'files.explorer.categories.comments',
  4: 'files.explorer.categories.photos',
  5: 'files.explorer.categories.documents',
  6: 'files.explorer.categories.other'
};

export function isHiddenStructuralFolder(folder: ExplorerFolder): boolean {
  if (!folder.isSystem || folder.type === undefined) {
    return false;
  }
  return !VISIBLE_SYSTEM_TYPES.has(folder.type);
}

export function folderLabelKey(folder: ExplorerFolder): string | undefined {
  if (folder.type !== undefined && TASK_SEMANTIC_LABEL_KEYS[folder.type]) {
    return TASK_SEMANTIC_LABEL_KEYS[folder.type];
  }
  if (folder.systemKey !== undefined && SYSTEM_KEY_LABEL_KEYS[folder.systemKey]) {
    return SYSTEM_KEY_LABEL_KEYS[folder.systemKey];
  }
  return undefined;
}

export function toFolderRow(folder: ExplorerFolder): FileExplorerFolderRow {
  return { folder, labelKey: folderLabelKey(folder) };
}

export function compareFilesBy(sortBy: FileExplorerSort, first: FileInfo, second: FileInfo): number {
  if (sortBy === 'date') {
    return (second.createdAt?.getTime() ?? 0) - (first.createdAt?.getTime() ?? 0);
  }
  if (sortBy === 'size') {
    return (second.size ?? 0) - (first.size ?? 0);
  }
  return (first.name ?? '').localeCompare(second.name ?? '');
}

/** Column sort state. `reversed` flips each column's natural order (name A→Z, newest first, largest first). */
export interface FileExplorerSortState {
  by: FileExplorerSort;
  reversed: boolean;
}

export function sortFiles(files: readonly FileInfo[], sort: FileExplorerSortState): FileInfo[] {
  const direction = sort.reversed ? -1 : 1;
  return [...files].sort((first, second) => direction * compareFilesBy(sort.by, first, second));
}

/** `aria-sort` for a column header; natural order of date/size is descending. */
export function ariaSortFor(
  column: FileExplorerSort,
  sort: FileExplorerSortState
): 'ascending' | 'descending' | 'none' {
  if (sort.by !== column) return 'none';
  const ascendingByDefault = column === 'name';
  return ascendingByDefault !== sort.reversed ? 'ascending' : 'descending';
}

const SIZE_UNITS = ['B', 'kB', 'MB', 'GB', 'TB'];

/** Decimal (SI) size as Finder shows it; undefined/invalid sizes return undefined so the caller shows a dash. */
export function formatFileSize(bytes: number | undefined, locale: string): string | undefined {
  if (bytes === undefined || bytes === null || !Number.isFinite(bytes) || bytes < 0) return undefined;
  let value = bytes;
  let unit = 0;
  while (value >= 1000 && unit < SIZE_UNITS.length - 1) {
    value /= 1000;
    unit++;
  }
  const formatted = new Intl.NumberFormat(locale, { maximumFractionDigits: unit === 0 ? 0 : 1 }).format(value);
  return `${formatted} ${SIZE_UNITS[unit]}`;
}
